import type { JSX } from 'react';
import { Link } from 'react-router-dom';
import { PublicPageHeading } from '@/components/public/PublicPageHeading';
import { PublicPageSection } from '@/layouts/PublicPageLayout';
import type { AboutValuesContent } from '@/lib/types/siteSettings';

const VALUE_PILLARS = [
  {
    title: 'Adaptive reuse',
    description:
      'We focus on reusing and upgrading existing buildings to cut embodied carbon while preserving character.',
  },
  {
    title: 'Efficient systems',
    description:
      'Reflective roofing, rooftop solar, geothermal HVAC, LED lighting, efficient plumbing, and storm-water harvesting deliver lasting performance.',
  },
  {
    title: 'Trouble-free tenancy',
    description:
      'We prioritize smooth operations so tenants can stay productive with fewer distractions and surprises.',
  },
] as const;

const DEFAULT_VALUES_CONTENT: AboutValuesContent = {
  title: 'Sustainability, service, and long-term value',
  description:
    'From adaptive reuse to tenant-first operations, we build and manage spaces designed for modern teams.',
  ctaLabel: 'View availabilities',
  ctaHref: '/availabilities',
  pillars: [...VALUE_PILLARS],
};

function preferText(value: string | null | undefined, fallback: string): string {
  if (typeof value === 'string' && value.trim().length > 0) {
    return value;
  }
  return fallback;
}

function preferList<T>(value: T[] | null | undefined, fallback: T[]): T[] {
  if (Array.isArray(value) && value.length > 0) {
    return value;
  }
  return fallback;
}

export function AboutValuesSection({
  content,
}: {
  content?: AboutValuesContent | null;
}): JSX.Element {
  const resolvedContent = {
    title: preferText(content?.title, DEFAULT_VALUES_CONTENT.title),
    description: preferText(content?.description, DEFAULT_VALUES_CONTENT.description),
    ctaLabel: preferText(content?.ctaLabel, DEFAULT_VALUES_CONTENT.ctaLabel),
    ctaHref: preferText(content?.ctaHref, DEFAULT_VALUES_CONTENT.ctaHref),
    pillars: preferList(content?.pillars, DEFAULT_VALUES_CONTENT.pillars),
  };

  return (
    <PublicPageSection tone="muted" spacing="md" aria-label="Strashin values">
      <PublicPageHeading
        title={resolvedContent.title}
        description={resolvedContent.description}
        layout="split"
        actions={
          <Link
            to={resolvedContent.ctaHref}
            className="inline-flex min-h-11 items-center justify-center rounded-full border border-brand-accent px-5 py-2 text-sm font-semibold text-brand-accent transition hover:border-brand-primary hover:bg-brand-primary/10 hover:text-brand-primary"
          >
            {resolvedContent.ctaLabel}
          </Link>
        }
      />

      <div className="grid gap-6 md:grid-cols-3">
        {resolvedContent.pillars.map((pillar) => (
          <article
            key={pillar.title}
            className="rounded-3xl border border-surface-subtle bg-white p-6 shadow-card"
          >
            <h3 className="text-lg font-semibold text-brand-primary">{pillar.title}</h3>
            <p className="mt-3 text-sm leading-relaxed text-text-primary/75">
              {pillar.description}
            </p>
          </article>
        ))}
      </div>
    </PublicPageSection>
  );
}
